import { useRef, useState } from "react";

import { MediaModels } from "@/data/media/media.models";
import { MediaQueries } from "@/data/media/media.queries";
import { ProfileQueries } from "@/data/profile/profile.queries";
import { logger } from "@/util/logger";
import { type ApplicationException, type GeneralErrorCodes, SharedErrorHandler } from "@/util/vendor/error-handling";

type FileUploadError = ApplicationException<GeneralErrorCodes>;
type FileUploadData = { url: string; id?: string };

interface IProps {
  onSuccess?: (data: FileUploadData) => void;
  onError?: (err: FileUploadError) => void;
}

type FileUploadState =
  | {
      state: "idle";
    }
  | {
      state: "uploading";
      progress: number;
    }
  | {
      state: "uploaded";
      data: FileUploadData;
    }
  | {
      state: "error";
      error: FileUploadError;
    };

export const useIntroductionCoverImageUpload = (use: MediaModels.Use, { onError, onSuccess }: IProps = {}) => {
  const useGetPresignedUrl = ProfileQueries.useRequestIntroductionCoverImageUpload();
  const useUploadPut = MediaQueries.useUploadPut();
  const useUploadPost = MediaQueries.useUploadPost();
  const verifyUpload = ProfileQueries.useValidateIntroductionCoverImageUpload();

  const [uploadState, setUploadState] = useState<FileUploadState>({
    state: "idle",
  });

  const abortController = useRef<AbortController | null>(null);

  const cancelUpload = () => {
    if (abortController) {
      abortController.current?.abort();
      abortController.current = null;
    }
  };

  const uploadFile = async (file: File) => {
    try {
      const presignedUrlData = (await useGetPresignedUrl.mutateAsync({
        data: {
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
        },
      })) as any;

      setUploadState({ state: "uploading", progress: 0 });

      abortController.current = new AbortController();
      if (!presignedUrlData?.url) {
        throw new Error("Upload URL is not provided");
      }
      const request: MediaModels.PutUploadRequest = {
        file,
        url: presignedUrlData.url,
        onUploadProgress: ({ loaded, total }) => {
          if (total == null) {
            return;
          }

          const progress = Math.round((loaded / total) * 100);
          setUploadState({ state: "uploading", progress });
        },
        abortController: abortController.current,
      };

      if (presignedUrlData?.method === MediaModels.UploadMethod.POST) {
        await useUploadPost.mutateAsync({
          ...request,
          fields: Object.fromEntries(presignedUrlData?.fields ?? []),
        });
      } else {
        await useUploadPut.mutateAsync(request);
      }

      const data = { url: presignedUrlData?.url || "", id: presignedUrlData?.id };
      if (presignedUrlData?.id) {
        const verifyResponse = await verifyUpload.mutateAsync({ data: { id: presignedUrlData.id } });
        data.url = verifyResponse.media?.url || "";
        data.id = presignedUrlData.id;
      }

      onSuccess?.(data);
      setUploadState({ state: "uploaded", data });
    } catch (err) {
      const error = SharedErrorHandler.getError(err)!;
      logger.error("Error uploading file", error);

      onError?.(error);
      setUploadState({ state: "error", error });
    } finally {
      abortController.current = null;
    }
  };

  return {
    uploadState,
    uploadFile,
    cancelUpload,
  };
};
